import { NOYAU_CODES, type NoyauCode } from '@rosetta/shared';
import { z } from 'zod';
import type { ConfigTahiti } from '../config';
import { ServiceIndisponibleError, SessionExpireeError } from '../erreurs';
import { analyser } from './analyse';
import type { ContexteRosetta } from './contexte';
import {
  CODE_LANGUE,
  dateTahitiSchema,
  idSchema,
  lireLignes,
  reponseCreationSchema,
} from './h2data';
import type { AppelHttp } from './http';

/**
 * The task calls. A translation is a `rose_task_v5_multi_translation` task (Rosetta v5)
 * created through TaskService, then started; its identifier is the identifier of the
 * translation. Every translation starts a real (paid) AI treatment: nothing here ever
 * retries or relaunches a task.
 *
 * The calls follow the reference script of the team (confirmed working): creation in status
 * `W` with `POST /api/v2/task/create`, start with `POST /api/v2/task/setStatus` (`R`), read in
 * the table of the tasks.
 */

export const TABLE_TACHE = 'rose_task_v5_multi_translation';

/** Field of the task that selects one noyau: `target_core_sgp` for `SGP`. */
export const champNoyau = (code: NoyauCode): string => `target_core_${code.toLowerCase()}`;

export const requeteCreationTacheSchema = z.object({
  type: z.literal(TABLE_TACHE),
  tableName: z.literal(TABLE_TACHE),
  title: z.string().min(1),
  main_lang: z.literal(CODE_LANGUE),
  category: z.string().min(1),
  version: z.literal(0),
  status: z.literal('W'),
  h2_workspace_id: z.number().int().positive(),
  h2_activity_id: z.number().int().positive(),
  h2_project_id: z.number().int().positive(),
  h2_tasktype_id: z.number().int().positive(),
  h2_bp_id: z.number().int().positive(),
  rose_document_id: z.number().int().positive(),
  rose_methodology_id: z.number().int().positive(),
  analysis_language_id: z.number().int().positive(),
  target_language_id: z.number().int().positive(),
  mode_traduction: z.literal('sequence'),
  execution_intent_code: z.literal('resume'),
  /** Legacy flags TaskService does not default: always sent, always false. */
  is_common_request: z.literal(false),
  is_final_request: z.literal(false),
  target_core_gps: z.boolean(),
  target_core_gsp: z.boolean(),
  target_core_pgs: z.boolean(),
  target_core_psg: z.boolean(),
  target_core_sgp: z.boolean(),
  target_core_spg: z.boolean(),
});
export type RequeteCreationTache = z.infer<typeof requeteCreationTacheSchema>;

/**
 * Creates the task of a translation (the document must exist), in status `W`, and returns
 * its identifier. It does not start it: see `lancerTache`.
 *
 * The body is the one of the reference script, except for the noyaux: the script switches
 * the six on, Rosetta switches on the chosen ones only and sends `false` for the others.
 *
 * TO VALIDATE(#6): that the creation of a task with a subset of the noyaux is accepted
 * (the script only ever sends the six).
 */
export async function creerTache(
  appel: AppelHttp,
  config: ConfigTahiti,
  jeton: string,
  contexte: ContexteRosetta,
  tache: { titre: string; documentId: string; noyaux: readonly NoyauCode[] },
): Promise<string> {
  const corps = requeteCreationTacheSchema.parse({
    type: TABLE_TACHE,
    tableName: TABLE_TACHE,
    title: tache.titre,
    main_lang: CODE_LANGUE,
    category: config.categorie,
    version: 0,
    status: 'W',
    h2_workspace_id: config.espaceTravailId,
    h2_activity_id: config.activiteId,
    h2_project_id: config.projetId,
    h2_tasktype_id: contexte.typeTacheId,
    h2_bp_id: contexte.processusId,
    rose_document_id: Number(tache.documentId),
    rose_methodology_id: contexte.methodologieId,
    analysis_language_id: contexte.langueId,
    target_language_id: contexte.langueId,
    mode_traduction: 'sequence',
    execution_intent_code: 'resume',
    is_common_request: false,
    is_final_request: false,
    ...Object.fromEntries(
      NOYAU_CODES.map((code) => [champNoyau(code), tache.noyaux.includes(code)]),
    ),
  });

  const reponse = await appel({
    methode: 'POST',
    url: `${config.urlH2data}/api/v2/task/create`,
    jeton,
    corps,
  });
  return analyser(reponseCreationSchema, reponse, 'task creation');
}

/** `R` starts a task, `C` cancels it (#7): the two letters Rosetta ever writes. */
export const requeteStatutTacheSchema = z.object({
  id: z.number().int().positive(),
  status: z.enum(['R', 'C']),
});

/** The answer of `setStatus` is only checked for a refusal, as the script does. */
const reponseStatutSchema = z.object({ success: z.boolean().optional() });

/**
 * Starts a created task: its status goes from `W` to `R`. This is the call that sets the
 * (paid) AI treatment going, so it is made once, right after the creation, and never again.
 *
 * TO VALIDATE(#6): the answer is only read for `success: false`, as in the script.
 */
export async function lancerTache(
  appel: AppelHttp,
  config: ConfigTahiti,
  jeton: string,
  id: string,
): Promise<void> {
  const corps = requeteStatutTacheSchema.parse({ id: Number(id), status: 'R' });
  const reponse = await appel({
    methode: 'POST',
    url: `${config.urlH2data}/api/v2/task/setStatus`,
    jeton,
    corps,
  });
  if (analyser(reponseStatutSchema, reponse, 'task start').success === false) {
    throw new ServiceIndisponibleError(`Tahiti refused to start the task ${id}`);
  }
}

/**
 * Cancels a task, or a sub-task of one (#7): its status goes to `C`, with the token of the user.
 * Cancelling the task of a translation does NOT stop its sub-tasks (seen on devdb, 2026-10-07: they
 * stayed `S`); each one is cancelled by its own call, see `annulerEnCascade` (`etapes.ts`).
 * A refusal other than an expired session (403 for a task the account may not touch, 400) is
 * reported as `ServiceIndisponibleError`, never as an expired session: the user is not logged out
 * for it.
 *
 * TO VALIDATE(#7): that `setStatus` to `C` is accepted for a task in `W`, `R` or `S`, by an
 * ordinary account, for the task of a translation and for its sub-tasks.
 */
export async function annulerTache(
  appel: AppelHttp,
  config: ConfigTahiti,
  jeton: string,
  id: string,
): Promise<void> {
  const corps = requeteStatutTacheSchema.parse({ id: Number(id), status: 'C' });
  const reponse = await appel({
    methode: 'POST',
    url: `${config.urlH2data}/api/v2/task/setStatus`,
    jeton,
    corps,
    surRefus: (statut) =>
      statut === 401
        ? new SessionExpireeError()
        : new ServiceIndisponibleError(`Tahiti refused to cancel the task ${id} (${statut})`),
  });
  if (analyser(reponseStatutSchema, reponse, 'task cancellation').success === false) {
    throw new ServiceIndisponibleError(`Tahiti refused to cancel the task ${id}`);
  }
}

const booleen = z.boolean().nullish();

export const ligneTacheSchema = z.object({
  id: idSchema,
  status: z.string().min(1),
  rose_document_id: idSchema,
  /** Creation instant: where the elapsed time of the waiting screen starts (#7). */
  created_at: dateTahitiSchema.nullish(),
  /**
   * The message Tahiti gives a task that went wrong ("Child tasks interrupted: 910344:I" on
   * devdb). Shown as it is when it is readable (#13, `messageLisible`).
   */
  error_message: z.string().nullish(),
  target_core_sgp: booleen,
  target_core_spg: booleen,
  target_core_psg: booleen,
  target_core_pgs: booleen,
  target_core_gps: booleen,
  target_core_gsp: booleen,
});
export type LigneTache = z.infer<typeof ligneTacheSchema>;

export interface TacheLue {
  id: string;
  /** Status letter of `h2_task` (`E`, `S`, ...): never interpreted alone, see `classerStatut`. */
  statut: string;
  documentId: string;
  /** The noyaux the task works on, in charter order. */
  noyaux: NoyauCode[];
  /** Creation instant (ISO 8601, UTC), when Tahiti gives it (#7). */
  creeLe?: string;
  /** The `error_message` of the task, as Tahiti wrote it, when there is one (#13). */
  messageErreur?: string;
}

/**
 * Reads a task, or `null` when there is none with this identifier.
 *
 * TO VALIDATE(#6): that the table of the v5 tasks carries `rose_document_id` and the
 * `target_core_*` flags as columns (the creation sends them, the script reads neither).
 */
export async function lireTache(
  appel: AppelHttp,
  config: ConfigTahiti,
  jeton: string,
  id: string,
): Promise<TacheLue | null> {
  const [ligne] = await lireLignes(
    appel,
    config,
    jeton,
    {
      table: TABLE_TACHE,
      fields: [
        'id',
        'status',
        'error_message',
        'rose_document_id',
        'created_at',
        ...NOYAU_CODES.map(champNoyau),
      ],
      where: ['id', Number(id), '='],
      limit: 1,
      lang: CODE_LANGUE,
    },
    ligneTacheSchema,
  );
  if (ligne === undefined) return null;

  const choisis = NOYAU_CODES.filter(
    (code) => ligne[champNoyau(code) as keyof LigneTache] === true,
  );
  return {
    id: ligne.id,
    statut: ligne.status,
    documentId: ligne.rose_document_id,
    // Tahiti's own rule: no noyau selected means the six of them.
    noyaux: choisis.length > 0 ? choisis : [...NOYAU_CODES],
    ...(ligne.created_at == null ? {} : { creeLe: ligne.created_at }),
    ...(ligne.error_message == null ? {} : { messageErreur: ligne.error_message }),
  };
}

/** The longest message of Tahiti shown as it is; a longer one reads like a dump, not a sentence. */
const LONGUEUR_MAX_MESSAGE = 300;

/** The start of a technical dump rather than a sentence: JSON, markup, a Python trace. */
const DEBUT_TECHNIQUE = /^(?:[{[<]|traceback\b)/i;

/**
 * The error message of Tahiti as the screen may show it, as it is (#13), or `undefined` when it is
 * not readable: empty, longer than 300 characters, several lines, or the look of a technical
 * dump (JSON, markup, a stack trace). Spaces are squeezed, nothing else is rewritten: "Child tasks
 * interrupted: 910344:I" is shown as Tahiti wrote it.
 */
export function messageLisible(brut: string | undefined): string | undefined {
  if (brut === undefined) return undefined;
  const net = brut.trim();
  if (net === '' || net.length > LONGUEUR_MAX_MESSAGE || /[\r\n]/.test(net)) return undefined;
  if (DEBUT_TECHNIQUE.test(net) || /\bat \S+ \(/.test(net)) return undefined;
  return net.replace(/\s+/g, ' ');
}

/**
 * How long a task may stay "En attente" (`W`) before it reads as never started (#13). Rosetta
 * creates the task in `W` and starts it (`R`) in the next call: a task still `W` after that is
 * one whose start failed (B7), and it would otherwise show "en cours" for ever. 15 minutes is
 * well beyond the few milliseconds between the two calls, and beyond a slow Tahiti: a decision
 * of the development, to confirm with Nicolas Melet.
 */
export const DELAI_DEMARRAGE_MS = 15 * 60 * 1000;

/**
 * A task created and never started: still "En attente" (`W`) `DELAI_DEMARRAGE_MS` after its
 * creation. No AI treatment ran for it: it reads as a failed translation (`nonDemarree`), and
 * Rosetta writes nothing to it (it is not cancelled: there is nothing to stop).
 */
export function jamaisDemarree(
  lettre: string,
  correspondance: CorrespondanceStatuts | undefined,
  creeLe: string | undefined,
  maintenant: number,
): boolean {
  if (creeLe === undefined || !estEnAttente(lettre, correspondance)) return false;
  return maintenant - Date.parse(creeLe) >= DELAI_DEMARRAGE_MS;
}

/* ── Status mapping ──────────────────────────────────────────────────────────────── */

export type StatutTache = 'en_cours' | 'terminee' | 'echouee' | 'interrompue';

/** Letter of `h2_task.status` -> its label in `h2_property_value` (`h2_task.status.<letter>`). */
export type CorrespondanceStatuts = ReadonlyMap<string, string>;

export const ligneStatutSchema = z.object({
  code: z.string().min(1),
  title: z.string().nullish(),
});

const PREFIXE_STATUT = 'h2_task.status.';

/**
 * Reads the status table of Tahiti (`h2_property_value`, codes `h2_task.status.*`).
 * Tahiti's instruction: never interpret the letter alone ("E means Terminé on dev").
 */
export async function lireCorrespondanceStatuts(
  appel: AppelHttp,
  config: ConfigTahiti,
  jeton: string,
): Promise<CorrespondanceStatuts> {
  const lignes = await lireLignes(
    appel,
    config,
    jeton,
    {
      table: 'h2_property_value',
      fields: ['id', 'code', 'title'],
      where: ['code', `${PREFIXE_STATUT}%`, 'LIKE'],
    },
    ligneStatutSchema,
  );
  return new Map(
    lignes
      .filter((ligne) => ligne.code.startsWith(PREFIXE_STATUT))
      .map((ligne) => [ligne.code.slice(PREFIXE_STATUT.length), ligne.title ?? ''] as const),
  );
}

/**
 * When the table cannot be read: the terminal statuses of the reference script, E finished
 * (the only success), I and F failed, C cancelled (#7). The other letters (W, R, S...) are a
 * task still waiting or running.
 */
const REPLI_TERMINEE = ['E'];
const REPLI_ECHOUEE = ['F', 'I'];
const REPLI_INTERROMPUE = ['C'];

const normaliser = (libelle: string): string =>
  libelle
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .trim()
    .toLowerCase();

/**
 * Is this status letter the one of a task Tahiti created and has not started ("En attente",
 * `W`)? The label decides when the table of the statuses gives one, the letter otherwise.
 */
export function estEnAttente(lettre: string, correspondance?: CorrespondanceStatuts): boolean {
  const libelle = correspondance?.get(lettre);
  return libelle !== undefined && libelle !== ''
    ? normaliser(libelle) === 'en attente'
    : lettre === 'W';
}

/** Labels (without accents, lower case) that end a task well, badly, or by a cancellation. */
const LIBELLES_TERMINEE = ['termine'];
const LIBELLES_ECHOUEE = ['ferme', 'interrompu'];
const LIBELLES_INTERROMPUE = ['annule'];

/**
 * Maps a task status to the four states of the application.
 *
 * With the table of Tahiti, the LABEL decides ("Terminé" finished; "Fermé" and "Interrompu"
 * failed; "Annulé" interrupted, #7; anything else still running). Without it (table
 * unreadable, or letter absent from it) the letters of the reference script apply: E
 * finished, I and F failed, C interrupted (a cancelled task would otherwise keep the screen
 * waiting for ever), the rest running.
 *
 * TO VALIDATE(#6): that the labels of `h2_property_value` still agree with the letters of the
 * v5 tasks; the reference script reads the letters only.
 */
export function classerStatut(lettre: string, correspondance?: CorrespondanceStatuts): StatutTache {
  const libelle = correspondance?.get(lettre);
  if (libelle !== undefined && libelle !== '') {
    const normalise = normaliser(libelle);
    if (LIBELLES_TERMINEE.includes(normalise)) return 'terminee';
    if (LIBELLES_ECHOUEE.includes(normalise)) return 'echouee';
    if (LIBELLES_INTERROMPUE.includes(normalise)) return 'interrompue';
    return 'en_cours';
  }
  if (REPLI_TERMINEE.includes(lettre)) return 'terminee';
  if (REPLI_ECHOUEE.includes(lettre)) return 'echouee';
  if (REPLI_INTERROMPUE.includes(lettre)) return 'interrompue';
  return 'en_cours';
}
